<template>
  <li data-component-name="TableOfContent.Item">
    <component
      :is="hasNestedItems ? 'button' : 'NuxtLink'"
      :to="!hasNestedItems ? to : null"
      :class="[
        type,
        device,
        { selected: activeId === to || showNestedItems || hasNestedActive },
      ]"
      @click="toggleNestedItems"
    >
      <span v-html="title" />

      <SvgIcon name="chevron-down" width="20" height="20" />
    </component>

    <Transition name="slide">
      <ul tag="ul" v-if="showNestedItems || hasNestedActive">
        <TableOfContentItem
          v-for="({ href, title }, index) in nested"
          :key="href"
          :to="href"
          :title
          :active-id
          :device
          type="secondary"
          @click="emit('closeMenu')"
        />
      </ul>
    </Transition>
  </li>
</template>

<script lang="ts" setup>
import { NuxtLink } from '#components';
import type { IContentTable } from '~/types';

defineOptions({
  components: {
    NuxtLink,
  },
});

interface TableOfContentItemProps {
  type?: 'primary' | 'secondary';
  device?: 'desktop' | 'mobile';

  to: string;
  title: string;

  nested?: IContentTable[];

  activeId: string | null;
}

const props = withDefaults(defineProps<TableOfContentItemProps>(), {
  type: 'primary',
  device: 'desktop',

  selected: false,
});

const emit = defineEmits(['closeMenu']);

const showNestedItems = ref(false);

const hasNestedItems = computed(() => props.nested && props.nested.length > 0);

const toggleNestedItems = () => {
  if (hasNestedItems.value) {
    showNestedItems.value = !showNestedItems.value;
  } else {
    emit('closeMenu');
  }
};

const hasNestedActive = computed(
  () => props.nested && props.nested.some(item => item.href === props.activeId),
);
</script>

<style lang="scss" scoped>
@import '$/mixins/typography';
@import '$/mixins/flex';
@import '$/functions/token';

[data-component-name='TableOfContent.Item'] {
  button {
    @include flex-center-start;
    color: token('text-secondary');

    width: 100%;
    text-align: start;

    transition: color 0.15s ease-in-out;

    &.desktop {
      &.primary {
        @include body-3;
        padding: 0.5rem;

        svg {
          display: none;
        }
      }

      &:hover {
        background-color: rgba(token('accent-hover'), 32%);
      }
    }

    &.mobile {
      &.primary {
        @include flex-center-sb;
        @include body-3;
        padding: 0.5rem;

        svg {
          transition: transform 300ms ease-in-out;
        }

        &.selected {
          svg {
            transform: rotate(-180deg);
          }
        }
      }
    }
  }

  a {
    @include flex-center-sb;

    color: token('text-secondary');

    transition: all 0.15s ease-in-out;

    &.primary {
      @include body-3;
      padding: 0.5rem;

      &.selected {
        background-color: rgba(token('accent-hover'), 16%);
        color: token('text-primary');
      }
    }

    &.secondary {
      @include body-4;
      padding: 0 0.5rem;

      border-left: solid 0.0625rem token('outline-secondary');

      &.selected {
        background-color: rgba(token('accent-hover'), 16%);
        color: token('text-primary');

        border-left: solid 0.1875rem token('text-action');
      }
    }

    &.desktop {
      &.primary {
        @include body-3;
      }

      &.secondary {
        @include body-4;
      }
    }

    &:hover {
      background-color: rgba(token('accent-hover'), 32%);
    }

    &:active {
      background-color: rgba(token('accent-hover'), 32%);
    }

    &.mobile {
      &.primary {
      }

      &.secondary {
      }
    }

    svg {
      display: none;
    }
  }

  :deep(strong, b) {
    font-weight: 400;
  }

  .slide-enter-active,
  .slide-leave-active {
    transition: all 150ms ease-in;
  }

  .slide-enter-from,
  .slide-leave-to {
    max-height: 0;
    opacity: 0;
  }

  .slide-enter-to {
    max-height: 30rem;
    opacity: 1;
  }

  .slide-leave-from {
    max-height: 30rem;
  }
}
</style>
